import React, { useEffect, useCallback } from "react";

import { constSelector } from "recoil";

import { ParsedJsonTableContentType } from "@/types/fillInTableContent.types";

import * as S from "./FillInTableArticleTable.style";

interface Props {
  item: ParsedJsonTableContentType;
  isModal?: boolean;
  searchTerm?: string;
  currentSearchIndex?: number;
  onSearchResultsCount?: (count: number) => void;
  onSearchRangesUpdate?: (ranges: Array<React.ReactElement>) => void;
  tableIndex: number;
}

const FillInTableArticleTable = ({
  item,
  isModal,
  searchTerm,
  currentSearchIndex,
  onSearchResultsCount,
  onSearchRangesUpdate,
  tableIndex,
}: Props) => {
  const parseHtmlToTags = (html: string) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const table = doc.querySelector("table");
    if (!table) return;

    const elementsArray: (string | null)[][] = [];

    const rows = table.querySelectorAll("tr");
    rows.forEach((row) => {
      const cells = row.querySelectorAll("td, th");
      const rowElements = Array.from(cells).map(
        (cell) => cell.textContent && cell.textContent.trim(),
      );
      elementsArray.push(rowElements);
    });

    return elementsArray;
  };

  const checkSameColumnEmptyData = (data: (string | null)[][]) => {
    const columnLength = data[0].length;
    const emptyColumnIndex: number[] = [];
    for (let i = 0; i < columnLength; i++) {
      const columnData = data.map((row) => row[i]);
      if (columnData.every((column) => !column)) {
        emptyColumnIndex.push(i);
      }
    }
    const removeEmptyColumnData = data.map((row) =>
      row.filter((_, index) => !emptyColumnIndex.includes(index)),
    );
    return removeEmptyColumnData;
  };

  const highlightSearchTerm = useCallback(
    (text: string, rowIndex: number) => {
      if (!searchTerm) return text;

      const escapedSearchTerm = searchTerm.replace(
        /[.*+?^${}()|[\]\\]/g,
        "\\$&",
      );
      const regex = new RegExp(escapedSearchTerm, "gi");
      let match = regex.exec(text);
      let lastIndex = 0;
      const parts = [];
      let searchIndex = 0;

      while (match !== null) {
        if (match.index > lastIndex) {
          parts.push(text.slice(lastIndex, match.index));
        }
        const isCurrentSearchResult = currentSearchIndex === searchIndex;

        const marked = (
          <mark
            key={`search-${match.index}-${match.index + searchTerm.length}`}
            data-start-index={match.index}
            data-end-index={match.index + searchTerm.length}
            data-table-key={`table-${tableIndex}`}
            data-table-row-index={rowIndex}
            style={{
              backgroundColor: "#6F768B",
              color: "white",
            }}
            className={`search-table-mark ${
              isCurrentSearchResult ? "current-search" : ""
            }`}
          >
            {text.slice(match.index, match.index + searchTerm.length)}
          </mark>
        );

        parts.push(marked);
        lastIndex = match.index + searchTerm.length;
        searchIndex++;
        match = regex.exec(text);
      }

      if (lastIndex < text.length) {
        parts.push(text.slice(lastIndex));
      }
      return parts;
    },
    [searchTerm, currentSearchIndex, item.html],
  );

  const tableData = checkSameColumnEmptyData(parseHtmlToTags(item.html) || []);

  useEffect(() => {
    if (searchTerm && item.html) {
      const ranges: Array<React.ReactElement> = [];
      tableData.forEach((row, rowIndex) => {
        row.forEach((cell) => {
          if (cell) {
            const markedElements = highlightSearchTerm(
              cell.replace(/\s+/g, " "),
              rowIndex,
            );

            if (Array.isArray(markedElements)) {
              markedElements.forEach((element) => {
                if (React.isValidElement(element) && element.type === "mark") {
                  ranges.push(element);
                }
              });
            }
          }
        });
      });

      if (onSearchResultsCount) {
        onSearchResultsCount(ranges.length);
      }
      if (onSearchRangesUpdate) {
        onSearchRangesUpdate(ranges);
      }
    } else {
      if (onSearchRangesUpdate) {
        onSearchRangesUpdate([]);
      }
      if (onSearchResultsCount) {
        onSearchResultsCount(0);
      }
    }
  }, [
    searchTerm,
    item.html,
    currentSearchIndex,
    onSearchResultsCount,
    onSearchRangesUpdate,
    highlightSearchTerm,
    tableData,
  ]);

  return (
    <S.FillInTableArticleTable isModal={isModal}>
      <table>
        <tbody>
          {tableData?.map((data, rowIndex) => (
            <tr key={`table-tr-${tableIndex}-${rowIndex}`}>
              {data?.map((dataItem, colIndex) => {
                return (
                  <td
                    key={`table-td-${tableIndex}-${rowIndex}-${colIndex}`}
                    className={`table-td-${tableIndex}-${rowIndex}-${colIndex}`}
                    data-table-key={`table-${tableIndex}`}
                    data-table-row-index={rowIndex}
                  >
                    {dataItem
                      ? highlightSearchTerm(
                          dataItem.replace(/\s+/g, " "),
                          rowIndex,
                        )
                      : null}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </S.FillInTableArticleTable>
  );
};

export default FillInTableArticleTable;
